import React from 'react';

// eslint-disable-next-line lodash/import-scope
import { assign, get } from 'lodash';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';

import ACControlPanelView from '../../components/amortization-calculator/ac-control-panel/ac-control-panel';
import ACResultsPanel from '../../components/amortization-calculator/ac-results-panel';
import { RELATED_ARTICLES } from '../../components/amortization-calculator/analytics-constants';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import Results from '../../components/currency/results/results';
import GuideCta from '../../components/guide-cta/guide-cta';
import type { MortgageCalculator } from '../../components/mortgage-calculator/shared';
import toolLinkConfig from '../../components/mortgage-calculator/shared/tool-link-config';
import { useUrlRecordContext } from '../../components/url-record';
import {
  getEventType,
  getEventDetails,
  getEventEntityName,
  getMortgageAmortizationJoinNW,
} from '../../lib/analytics-constants';
import type { Browser } from '../../lib/types';
import {
  ReducerInputsData,
  AmortizationCalculatorField,
} from '../../lib/utils';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

type Validations = Pick<
  ReducerInputsData<AmortizationCalculatorField>,
  'validations'
>;
export interface ACResultsProps {
  browser: Browser;
  app?: string;
  dispatch: (any) => void;
  mortgageCalculator?: MortgageCalculator;
  history: Record<string, any>;
  identity: Record<string, any>;
  location: Record<string, any>;
  validations?: Validations;
  onboardingBypass: boolean;
  route: Record<string, any>;
}

const ACResults: React.FC<ACResultsProps> = (props) => {
  const data = {
    ...props.mortgageCalculator,
    validations: props.validations,
  };

  const { identity, route, history, location } = props;

  const { title, synopsis } = useUrlRecordContext();

  const resultsPanelProps = {
    data,
    ...props,
  };

  const onClick = () => {
    if (location.pathname.indexOf('calculate-amortization-schedule') !== -1) {
      return;
    }

    history.pushState(
      {
        postOnboarding: true,
      },
      `${location.pathname}/calculate-amortization-schedule${location.search}${location.hash}`
    );
  };

  const getEvent = getMortgageAmortizationJoinNW();

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name="mortgages_amortization_calculator">
        <Impression
          eventType="page_impression"
          entityName={
            get(route, 'name') === 'default'
              ? 'mortgages_amortization_calculator_form'
              : 'mortgages_amortization_calculator'
          }
          onClick={onClick}
        >
          <Results
            {...assign({}, props, {
              className: 'ac-app',
              // @ts-ignore - ignore error
              controlPanel: <ACControlPanelView data={data} />,
              data,
              heading: title,
              subcopy: synopsis,
              lenders: (
                <LenderCards
                  trackingFormName="ac"
                  explanation="Know how much you qualify for."
                  namedQuery="mortgages_offer_ac"
                  analyticsTrackingConfig={{
                    entityName: 'mortgages_amortization_calculator_offer',
                    sectionName: 'mortgages_amortization_calculator_offer',
                    sectionPosition: 2,
                  }}
                  cardsPerRow={3}
                />
              ),
              membershipCta: (
                <GuideCta
                  isSignedIn={identity.isSignedIn}
                  impressionPayload={{
                    eventType: getEventType(getEvent),
                    entityName: getEventEntityName(getEvent),
                    payload: getEventDetails(getEvent),
                  }}
                />
              ),
              resultsPanel: <ACResultsPanel {...resultsPanelProps} />,
              methodology: (
                <Methodology
                  trackingFormName="ac-methodology"
                  impressionTracking
                />
              ),
              relatedArticles: (
                <RelatedArticles
                  entityName={RELATED_ARTICLES.entityName}
                  sectionName={RELATED_ARTICLES.sectionName}
                  sectionPosition={RELATED_ARTICLES.sectionPosition}
                  articles={RELATED_ARTICLES.articles}
                />
              ),
              toolLinkCards: (
                <ToolLinkCards
                  header="Other NerdWallet Resources"
                  impressionTrackingName="mortgages_amortization_tool_card"
                  data={data}
                  cards={toolLinkConfig.cards}
                  analyticsConfig={{
                    sectionName: 'mortgages_amortization_tool_card',
                    sectionPosition: 7,
                  }}
                />
              ),
            })}
          >
            <div />
          </Results>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

export default connect((state) => state)(ACResults);
